import {defineStore} from 'pinia'
import api from '@/services/api'
import {useSelectMenuOptionsStore} from '@/stores/selectMenuOptions'
import {useComponentsStore} from '@/stores/components'
import {useUserStore} from '@/stores/user'
import {useTemplatesStore} from '@/stores/templates'
import {useThemesStore} from '@/stores/themes'
import {useCategoriesStore} from '@/stores/categories'
import {usePagesStore} from '@/stores/pages'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useItemDataStore} from '@/stores/itemData'

export const useModifiedItemsStore = defineStore('modifiedItems', {
    state: () => ({
        requestLastSent: Math.ceil(Date.now() / 1000),
        requestInProgress: false,
        recentlyModified: [],
    }),
    actions: {
        async clearModifiedItems() {
            if (this.requestInProgress) return false
            this.requestInProgress = true
            
            // Prevent multiple requests in quick succession
            setTimeout(() => this.requestInProgress = false, 200)
            
            const {data: {items}} = await api.get('api/component/activity', {
                params: {createdDate: {ge: this.requestLastSent}}
            })
            
            this.requestLastSent = Math.ceil(Date.now() / 1000)
            
            const selectMenuOptionsStore = useSelectMenuOptionsStore()
            const componentsStore = useComponentsStore()
            const userStore = useUserStore()
            const itemDataStore = useItemDataStore()
            
            for (const o of items) {
                const {id, componentId, rowId: itemId, createdBy: userId, activity} = o
                
                if (this.recentlyModified.indexOf(id) > -1) return
                
                this.recentlyModified.push(id)
                
                // Limit history to last 100 items
                if (this.recentlyModified.length > 100) {
                    this.recentlyModified = this.recentlyModified.slice(-100)
                }
                
                const component = componentsStore.items.find(item => item.id === componentId)
                if (!component) continue
                
                const tableName = component.tableName
                
                await itemDataStore.updateDisplayOrders({componentId})
                
                await this.processTableName({tableName, id: itemId, userId, activity})
                
                selectMenuOptionsStore.clearData({tableName})
                
                if (userStore.user?.id !== o.createdBy) {
                    await this.updateRelationshipManyToManyData({tableName, componentId})
                }
            }
        },
        
        async updateRelationshipManyToManyData({tableName, componentId}) {
            const categoriesStore = useCategoriesStore()
            const componentStructureStore = useComponentStructureStore()
            const structure = componentStructureStore.get(componentId) || []
            
            structure
                .filter(o => o.type === 'relationshipManyToMany')
                .forEach(({columnName}) => {
                    categoriesStore.setData({tableName, columnName, data: undefined})
                })
        },
        
        async processTableName({tableName, id, userId, activity}) {
            if (tableName === 'templates_templates') {
                const templatesStore = useTemplatesStore()
                await templatesStore.init(true)
            }
            
            if (tableName === 'pages') {
                const pagesStore = usePagesStore()
                await pagesStore.getClassNames({refresh: true})
            }
            
            const itemDataStore = useItemDataStore()
            const isItemSet = await itemDataStore.isset({tableName, id})
            
            if (activity === 'Update' && isItemSet) {
                await itemDataStore.get({tableName, id, refresh: true})
                
                const themesStore = useThemesStore()
                themesStore.onModifiedItems({userId, tableName, id})
            }
        }
    }
})